import React, { useState } from "react";
import { X, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";

export default function ShareDialog({ title, text, shareUrl, ogUrl, onClose }) {
  const [copied, setCopied] = useState(false);

  // Use the OG-wrapped URL when sharing externally so link previews work.
  const linkToShare = ogUrl || shareUrl;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(linkToShare);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the link below.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: linkToShare });
      } catch {}
    } else {
      copy();
    }
  };

  const enc = encodeURIComponent;
  const intents = [
    { label: "WhatsApp", href: `https://wa.me/?text=${enc(`${text} ${linkToShare}`)}`, testid: "share-whatsapp" },
    { label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(linkToShare)}`, testid: "share-twitter" },
    { label: "Telegram", href: `https://t.me/share/url?url=${enc(linkToShare)}&text=${enc(text)}`, testid: "share-telegram" },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(linkToShare)}`, testid: "share-facebook" },
    { label: "Email", href: `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${linkToShare}`)}`, testid: "share-email" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Share</div>
          <button onClick={onClose} data-testid="close-share" className="text-[#a3a3a3] hover:text-[#f5f5f5]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mb-2">Link</div>
            <div className="flex gap-2">
              <input
                readOnly
                value={linkToShare}
                onClick={(e) => e.target.select()}
                data-testid="share-link-input"
                className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] outline-none px-3 py-2 text-xs font-mono text-[#a3a3a3]"
              />
              <button onClick={copy} data-testid="copy-link-btn" className="px-3 py-2 bg-[#ff4500] hover:bg-[#ff5722] text-white inline-flex items-center gap-2">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="text-[10px] uppercase tracking-[0.2em]">{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>

          {typeof navigator !== "undefined" && navigator.share && (
            <button onClick={nativeShare} data-testid="native-share-btn" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]">
              <Send className="w-4 h-4" /> Share via your apps
            </button>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mb-2">Or post to</div>
            <div className="grid grid-cols-2 gap-2">
              {intents.map((it) => (
                <a
                  key={it.label}
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={it.testid}
                  className="px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs text-center transition-colors"
                >
                  {it.label}
                </a>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-[#a3a3a3] font-mono uppercase tracking-[0.15em] leading-relaxed pt-2 border-t border-[#2a2a2a]">
            // This link unfurls to a rich preview (thumbnail + title) on WhatsApp, X, iMessage, Telegram, Discord, Slack.
          </p>
        </div>
      </div>
    </div>
  );
}
